import { syncLinkedSplits, resetLinkedSync } from "./linked_sync";
export { getLinkedSyncStatus, subscribeLinkedSync } from "./linked_sync";
import { reconcileSplitShares } from "./repo";
import { parseFamilyView } from "./family";
import { recoverExpenseMoves } from "./expense_move";
/**
 * The sync engine — the only code that talks to the backend.
 *
 * It never sits in a render path (W-2) and is never awaited by capture (INV-4).
 * The UI reads Dexie; this drains a queue in the background.
 *
 * Rows travel as **plain JSON** to Aegae's own Pages Function, which stores them
 * in Aegae's own D1 under the session's `account_id`. There is no vault, no
 * passphrase and no derived key — the app cannot lock you out of your own
 * ledger, and no salt rotation in another app can orphan it. The cost is that
 * the server can read your expenses; the whole of what stops another *user*
 * reading them is the backend scoping every query to your account, which is why
 * `server/isolation.test.ts` is the most important suite in the repo.
 *
 * INV-7: a failed push leaves `_dirty` set and surfaces the failure. The flag
 * is never cleared on error — an expense that silently failed to upload is a
 * user losing data without being told.
 */

import type { Table } from "dexie";

import { SessionExpiredError, CLIENT_PROTOCOL_VERSION, ProtocolMismatchError, SyncHttpError, FamilyHttpError, getFamily, adoptSessionExpiry, assertProtocolCompatible, protocolUpdateRequired, readSession } from "./backend";
import { db, familyDb, localLedgerAccountId, getMeta, setMeta } from "./db";
import { familyLedger, personalLedger, type LedgerRef } from "./ledger";
import {
  type SplitGroup,
  type SplitExpense,
  type SplitTransfer,
  fromWireSplitGroup,
  fromWireSplitExpense,
  fromWireSplitTransfer,
  type Budget,
  type Category,
  type Expense,
  type Profile,
  type RecurringRule,
  type Salary,
  type Sounding,
  type SoundingAction,
  type SyncMeta,
  fromWireBudget,
  fromWireCategory,
  fromWireExpense,
  fromWireProfile,
  getDroppedGridOrderCount,
  fromWireRecurringRule,
  fromWireSalary,
  fromWireSounding,
  fromWireSoundingAction,
  unknownFields,
} from "./types";
import { readCachedEntitlement, refreshEntitlement } from "./entitlement";
import { API_BASE } from "./api_base";
import type { OutboxStore } from "./outbox";
import { toWirePayload } from "./synced_tables";
import { createSyncRetry } from "./sync_retry";

const PUSH_BATCH = 200;
const PULL_LIMIT = 500;
const QUARANTINE_LIMIT = 100;
const CURSOR_KEY = "sync:since";

export type SyncState = "idle" | "syncing" | "offline" | "signed-out" | "error";

/**
 * The session was rejected mid-sync.
 *
 * Distinct from a generic failure because the remedy is different and the
 * generic message would be a lie: "will upload automatically" is false when the
 * credential is dead. The user has to sign in again.
 */
export { SessionExpiredError } from "./backend";

export interface SyncStatus {
  state: SyncState;
  /** Rows written locally but not yet accepted by the server. */
  pending: number;
  moveRecoveryFailures?: number;
  lastSyncedAt: string | null;
  /**
   * Rows the last pull could not parse and skipped. Reported, never hidden —
   * see `pullAll`.
   */
  unreadable: number;
  /** Derived split shares the last personal reconciliation could not update. */
  splitShareFailures?: number;
  /** Optional preferences dropped while retaining their otherwise valid rows. */
  droppedPreferences?: number;
  /**
   * Dirty rows the last push refused to upload because they belong to a
   * different account. Should always be 0 — see `pushSpec`.
   */
  foreign: number;
  held: number;
  /** User-facing message: what happened AND what happens next (C-8). */
  error: string | null;
  /** Whether this failure will be retried on a timer. */
  retrying: boolean;
}

const CLEAN_STATUS: SyncStatus = {
  state: "idle",
  pending: 0,
  moveRecoveryFailures: 0,
  lastSyncedAt: null,
  unreadable: 0,
  splitShareFailures: 0,
  droppedPreferences: 0,
  foreign: 0,
  held: 0,
  error: null,
  retrying: false,
};

let status: SyncStatus = CLEAN_STATUS;
const heldRows = new Set<string>();
const listeners = new Set<(s: SyncStatus) => void>();

export function getSyncStatus(): SyncStatus {
  return status;
}

export function subscribeSync(fn: (s: SyncStatus) => void): () => void {
  listeners.add(fn);
  fn(status);
  return () => listeners.delete(fn);
}

function setStatus(patch: Partial<SyncStatus>): void {
  status = { ...status, ...patch };
  for (const fn of listeners) fn(status);
}

/* ── table specifications ──────────────────────────────────────────────────── */

interface Syncable {
  id: string;
  updatedAt: string;
  deletedAt?: string | null;
}

interface SyncSpec<T extends Syncable> {
  /** Local Dexie table name, for the cursor and diagnostics. */
  readonly name: string;
  /**
   * Remote `table_name`.
   *
   * Unprefixed, because there is nothing to collide with any more. These used to
   * carry an `aegae_` prefix: leeseidon had its own `expenses` table on the same
   * shared account, and without the prefix each app would have decrypted the
   * other's rows and rendered a foreign schema as its own. That was INV-3b, and
   * it retired with the shared database. `server/router.ts` holds the matching
   * allowlist and refuses anything not on it.
   */
  readonly remote: string;
  readonly table: () => Table<T & SyncMeta, string>;
  /** Validate a payload before it reaches Dexie (W-5). */
  readonly fromWire: (input: unknown) => T;
  /**
   * Which account this row belongs to — the push guard's only question.
   *
   * `profile` is the odd one out: it has no `userId` column because the row is
   * *keyed* on the account id (`updateProfile` uses `requireUser()`). Reading
   * `userId` off it would be `undefined` and every profile push would be
   * skipped as foreign, which is why this is per-spec rather than one shared
   * property lookup.
   *
   * Method syntax, not a property arrow, and deliberately: it makes the
   * parameter bivariant so `specs` can still be walked as `SyncSpec<Syncable>`
   * the way `pushSpec` and `specForRemote` already do. Sound at runtime because
   * a spec's `ownerOf` only ever sees rows from that spec's own table.
   */
  ownerOf(row: T): string;
}

const specs: [
  SyncSpec<Profile>,
  SyncSpec<Category>,
  SyncSpec<Budget>,
  SyncSpec<Salary>,
  SyncSpec<RecurringRule>,
  SyncSpec<Expense>,
  SyncSpec<Sounding>,
  SyncSpec<SoundingAction>,
  SyncSpec<SplitGroup>,
  SyncSpec<SplitExpense>,
  SyncSpec<SplitTransfer>,
] = [
  { name: "profile", remote: "profile", table: () => db.profile, fromWire: fromWireProfile, ownerOf: (r) => r.id },
  { name: "categories", remote: "categories", table: () => db.categories, fromWire: fromWireCategory, ownerOf: (r) => r.userId },
  { name: "budgets", remote: "budgets", table: () => db.budgets, fromWire: fromWireBudget, ownerOf: (r) => r.userId },
  { name: "salaries", remote: "salaries", table: () => db.salaries, fromWire: fromWireSalary, ownerOf: (r) => r.userId },
  { name: "recurringRules", remote: "recurring_rules", table: () => db.recurringRules, fromWire: fromWireRecurringRule, ownerOf: (r) => r.userId },
  { name: "expenses", remote: "expenses", table: () => db.expenses, fromWire: fromWireExpense, ownerOf: (r) => r.userId },
  { name: "soundings", remote: "soundings", table: () => db.soundings, fromWire: fromWireSounding, ownerOf: (r) => r.userId },
  { name: "soundingActions", remote: "sounding_actions", table: () => db.soundingActions, fromWire: fromWireSoundingAction, ownerOf: (r) => r.userId },
  { name: "splitGroups", remote: "split_groups", table: () => db.splitGroups, fromWire: fromWireSplitGroup, ownerOf: (r) => r.userId },
  { name: "splitExpenses", remote: "split_expenses", table: () => db.splitExpenses, fromWire: fromWireSplitExpense, ownerOf: (r) => r.userId },
  { name: "splitTransfers", remote: "split_transfers", table: () => db.splitTransfers, fromWire: fromWireSplitTransfer, ownerOf: (r) => r.userId },
];

/** Each stream owns its store, owner, cursor, held rows and status. */
interface Stream {
  ledger: LedgerRef;
  path: string;
  specs: SyncSpec<Syncable>[];
  held: Set<string>;
  droppedPreferences: number;
}
function streamFor(ledger: LedgerRef): Stream {
  const family = ledger.kind === "family";
  const allowed = new Set(["profile", "categories", "budgets", "recurring_rules", "expenses"]);
  return { ledger, droppedPreferences: 0, path: family ? "/v1/family/sync" : "/v1/sync",
    specs: specs.filter(spec => !family || allowed.has(spec.remote)).map(spec => ({
      ...spec, table: () => ledger.db.table(spec.name),
    })), held: family ? new Set() : heldRows };
}
export type FamilySyncStatus = Omit<SyncStatus, "pending"> & { pending: number | null };
let familyStatus: FamilySyncStatus = { ...CLEAN_STATUS };
const familyListeners = new Set<(s: FamilySyncStatus) => void>();
export function getFamilySyncStatus(): FamilySyncStatus { return familyStatus; }
export function resetFamilySyncStatus(): void { setFamilyStatus({ ...CLEAN_STATUS }); }
export function subscribeFamilySync(fn: (s: FamilySyncStatus) => void): () => void {
  familyListeners.add(fn); fn(familyStatus); return () => familyListeners.delete(fn);
}
function setFamilyStatus(patch: Partial<FamilySyncStatus>): void {
  familyStatus = { ...familyStatus, ...patch };
  for (const fn of familyListeners) fn(familyStatus);
}
function streamStatus(stream: Stream, patch: Partial<SyncStatus>): void {
  if (stream.ledger.kind === "family") setFamilyStatus(patch); else setStatus(patch);
}
export const FAMILY_VIEW_KEY = "family:view";
export const FAMILY_ID_KEY = "family:id";
export const FAMILY_REMOVED_KEY = "family:removed";
const emptyRemovalResolutions = new Map<string, Promise<boolean>>();
export async function resolveEmptyRemoval(id: string): Promise<boolean> {
  const existing = emptyRemovalResolutions.get(id);
  if (existing) return existing;
  const resolution = (async () => {
    if (!await familyDb.meta.get(FAMILY_REMOVED_KEY)) return true;
    if (await countPending(familyLedger(id))) return false;
    const cached = await getMeta(FAMILY_VIEW_KEY);
    const name = cached ? parseFamilyView(JSON.parse(cached)).name : "the family";
    await (await import("./session")).resolveRemovedFamilyRows("discard");
    (await import("./family_notice")).announceFamilyRemoval(name);
    return true;
  })();
  emptyRemovalResolutions.set(id, resolution);
  try { return await resolution; }
  finally { emptyRemovalResolutions.delete(id); }
}
async function checkStreamResponse(res: Response, stream: Stream): Promise<void> {
  if (stream.ledger.kind === "family" && res.status === 403) {
    const body = await res.clone().json() as { error?: string };
    if (body.error === "not_a_member") {
      await stream.ledger.db.meta.put({ key: FAMILY_REMOVED_KEY, value: stream.ledger.ownerId });
      throw new FamilyHttpError(403, "not_a_member");
    }
  }
}

/**
 * The remote table names this client pushes.
 *
 * Exported for one reason: `server/router.ts` holds the matching allowlist, and
 * a name in one list and not the other means every push 400s with
 * `unknown_table` and then retries forever — taking the inbound pull down with
 * it, because `pushSpec` throws before `pullAll` runs. `sync.test.ts` reads the
 * server's list out of its source and asserts the two are identical.
 */
export const SYNCED_REMOTE_TABLES: readonly string[] = specs.map((s) => s.remote);

/** Local name → remote name, as the specs declare it. Must equal `SYNCED_TABLES` (sync.test.ts). */
export const SYNC_SPEC_TABLES: Readonly<Record<string, string>> = Object.fromEntries(
  specs.map((s) => [s.name, s.remote]),
);

/** What the outbox refill needs from a spec: where the row lives and how to validate it. */
export interface RemoteTable {
  readonly table: () => Table<Syncable & SyncMeta, string>;
  readonly fromWire: (input: unknown) => Syncable;
  ownerOf(row: Syncable): string;
}

export function remoteTable(remote: string, ledger?: LedgerRef): RemoteTable | null {
  if (ledger) return (streamFor(ledger).specs.find(s => s.remote === remote) as RemoteTable | undefined) ?? null;
  return specForRemote(remote);
}

function specForRemote(remote: string): SyncSpec<Syncable> | null {
  const found = specs.find((s) => s.remote === remote);
  return (found as SyncSpec<Syncable> | undefined) ?? null;
}

/* ── wire types ────────────────────────────────────────────────────────────── */

interface PushRow {
  table: string;
  row_id: string;
  updated_at: string;
  deleted: boolean;
  payload: string;
}

interface PullRow {
  table: string;
  row_id: string;
  seq: number;
  updated_at: string;
  deleted: boolean;
  payload: string;
  created_by_member?: string;
  updated_by_member?: string;
}

async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const session = readSession();
  if (session === null) throw new Error("Not signed in");

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      authorization: `Bearer ${session.token}`,
      "content-type": "application/json",
      "x-tally-protocol": String(CLIENT_PROTOCOL_VERSION),
    },
  });

  // Every authenticated response carries the session's refreshed expiry. This is
  // the only thing that makes the server-side slide reach the client.
  adoptSessionExpiry(response);
  return response;
}

/* ── the native outbox ──────────────────────────────────────────────────────── */

/**
 * The native outbox, where there is one (`main.tsx` sets it on the phone; the
 * web has none). Acknowledging is best-effort and loud: a failed ack leaves an
 * entry the next refill finds already uploaded and drops (`outbox.ts`), so it
 * costs a little work later and never data — it must not fail a sync.
 */
let outboxStore: OutboxStore | null = null;

export function setSyncOutbox(store: OutboxStore | null): void {
  outboxStore = store;
}

async function ackOutbox(acks: { table: string; rowId: string; updatedAt: string }[]): Promise<void> {
  if (outboxStore === null || acks.length === 0) return;
  try {
    await outboxStore.ack(acks);
  } catch (cause) {
    console.warn(
      "Could not acknowledge uploaded rows in the native outbox:",
      cause instanceof Error ? cause.message : cause,
    );
  }
}

/* ── push ──────────────────────────────────────────────────────────────────── */

/**
 * Upload this table's dirty rows. Returns how many were **refused** because they
 * belong to a different account.
 *
 * That count should always be zero: `adoptReauthenticatedSession` wipes the
 * device when a different account signs in over an existing ledger. This is the
 * net under that single comparison, and it is worth having because the local
 * database is one un-partitioned set of tables while the server stamps
 * `account_id` from whichever bearer token it is handed — so an unfiltered push
 * is the whole distance between "one device, one account" and one person's
 * expenses filed under another person's name. There is no undoing that server-side.
 */
async function pushSpec<T extends Syncable>(spec: SyncSpec<T>, accountId: string, stream: Stream): Promise<number> {
  const all = await spec.table().where("_dirty").equals(1).toArray();

  // Refused rows are left dirty and counted, never deleted (INV-7). Dropping
  // them would be silent data loss, and this is a state that cannot legitimately
  // occur — so it is said out loud, and permanently, rather than tidied away.
  const dirty = all.filter((row) => spec.ownerOf(row) === accountId);
  const foreign = all.length - dirty.length;
  if (dirty.length === 0) return foreign;

  for (let i = 0; i < dirty.length; i += PUSH_BATCH) {
    const chunk = dirty.slice(i, i + PUSH_BATCH);

    const rows: PushRow[] = chunk.map((row) => ({
      table: spec.remote,
      row_id: row.id,
      updated_at: row.updatedAt,
      // A tombstone still carries its row so another device can tell WHAT was
      // deleted, not merely that something was.
      deleted: row.deletedAt != null,
      // Bookkeeping stripped, unknown fields carried back up — one function,
      // shared with the native outbox so both send the same bytes.
      payload: toWirePayload(row),
    }));

    const res = await authedFetch(`${stream.path}/push`, {
      method: "POST",
      headers: { "x-tally-protocol": String(CLIENT_PROTOCOL_VERSION) },
      body: JSON.stringify({ rows }),
    });
    const etag = res.headers.get("x-entitlement-etag");
    if (stream.ledger.kind === "personal" && etag && readCachedEntitlement(accountId)?.etag !== etag) {
      void refreshEntitlement().catch((cause: unknown) => console.warn("Could not refresh the changed entitlement:", cause));
    }
    await checkStreamResponse(res, stream);
    if (res.status === 401) throw new SessionExpiredError();
    if (!res.ok) {
      // Leave every row in this chunk dirty; they retry next sync. Clearing the
      // flag here would lose the user's data silently.
      throw new SyncHttpError(`Could not upload ${spec.name} (HTTP ${String(res.status)})`, res.status);
    }

    const result = (await res.json()) as {
      accepted?: { row_id: string }[];
      rejected?: { row_id: string; reason: string; current_updated_at?: string }[];
    };
    for (const row of result.rejected ?? []) if (row.reason === "lapsed") stream.held.add(`${spec.remote}:${row.row_id}`);
    streamStatus(stream, { held: (stream.ledger.kind === "family" ? familyStatus.held : status.held) + (result.rejected ?? []).filter((row) => row.reason === "lapsed").length });
    const accepted = new Set((result.accepted ?? []).map((a) => a.row_id));

    // A rejection whose `current_updated_at` IS the version we sent is not a
    // conflict: the server already holds exactly these bytes — the native
    // uploader (a widget, an intent) pushed them first. Treated as accepted, so
    // the row goes clean instead of waiting for a pull to overwrite it with
    // itself (DATA_MODEL §5.4). Only an equal timestamp: a newer one is a real
    // loss, and the pull brings the winner down.
    const sentAt = new Map(chunk.map((row) => [row.id, row.updatedAt]));
    for (const rejection of result.rejected ?? []) {
      if (rejection.reason === "stale_update" && rejection.current_updated_at === sentAt.get(rejection.row_id)) {
        accepted.add(rejection.row_id);
      }
    }

    // Compare-and-set, NOT a blind write-back of `chunk`.
    //
    // `chunk` is a snapshot taken before the upload. Writing it back after the
    // round-trip would revert anything the user changed in between — and mark
    // it clean, so it would never be retried. The window is ordinary: sync
    // fires 1.5s after a write and the undo toast lives 5s, so Undo is on
    // screen while a push is in flight. Pinned by sync.test.ts.
    const syncedAt = new Date().toISOString();
    await stream.ledger.db.transaction("rw", spec.table(), async () => {
      for (const row of chunk) {
        if (!accepted.has(row.id)) continue;
        const current = await spec.table().get(row.id);
        if (!current || current.updatedAt !== row.updatedAt) continue;
        await spec.table().put({ ...current, _dirty: 0, _syncedAt: syncedAt });
      }
    });

    // The native outbox drops what the server has — the accepted version only
    // (`ack` compares `updatedAt`), never a rejected row and never one the user
    // changed while this push was in the air.
    await ackOutbox(
      chunk
        .filter((row) => accepted.has(row.id))
        .map((row) => ({ table: spec.remote, rowId: row.id, updatedAt: row.updatedAt, ...(stream.ledger.kind === "family" ? { ledger: "family" as const } : {}) })),
    );

    // A rejection means the server holds a newer row. It stays dirty only until
    // the pull below overwrites it — the server's copy wins, which is the same
    // last-write-wins rule applied server-side.
  }

  return foreign;
}

/* ── pull ──────────────────────────────────────────────────────────────────── */

/**
 * Import one row, or report that it could not be read.
 *
 * Returns true if the row was handled, false if it was quarantined.
 */
async function importRow(row: PullRow, stream: Stream, expenseDiagnostics: Map<string, string[]>): Promise<boolean> {
  const spec = stream.specs.find(spec => spec.remote === row.table) ?? null;
  // A table this client does not know. Not an error: an older install talking to
  // a newer account will see tables it has no schema for, and skipping them is
  // exactly right.
  if (spec === null) return true;

  let domain: Syncable;
  let extra: Record<string, unknown> | undefined;
  try {
    // Validate BEFORE Dexie (W-5). A malformed row is an error, never something
    // to coerce into shape.
    const raw: unknown = JSON.parse(row.payload);
    const before = getDroppedGridOrderCount();
    domain = spec.fromWire(raw);
    if (stream.ledger.kind === "family" && "splitRef" in domain && domain.splitRef != null) throw new Error("Split shares belong to personal ledgers.");
    stream.droppedPreferences += getDroppedGridOrderCount() - before;
    if (stream.ledger.kind === "family" && spec.ownerOf(domain) !== stream.ledger.ownerId) throw new Error(`Foreign owner in ${row.table}; expected ${stream.ledger.ownerId}.`);
    if (stream.ledger.kind === "family" && (typeof row.created_by_member !== "string" || typeof row.updated_by_member !== "string")) throw new Error("Family attribution must contain member ids.");
    if (stream.ledger.kind === "family" && domain.id !== row.row_id) throw new Error("Payload id does not match the sync envelope.");
    // …and keep what validation does not cover, rather than dropping it.
    extra = unknownFields(raw, domain);
  } catch (cause) {
    // Quarantine, do not abort (F4).
    //
    // This used to throw, which killed the whole sync. Rows arrive in `seq`
    // order, so a single unreadable row blacked out every row behind it — that
    // is precisely how 400+ expenses disappeared on 2026-07-22, and the thrown
    // error carried an empty message, so the UI said only ". Your expenses are
    // saved on this device…". The count is surfaced instead; one bad row can
    // never again hide the good ones.
    console.warn(
      `Skipped an unreadable ${row.table} row (${row.row_id}):`,
      cause instanceof Error ? cause.message : cause,
    );
    try {
      const meta = stream.ledger.db.meta;
      const key = `sync:quarantine:${String(row.seq)}`;
      await meta.put({ key, value: JSON.stringify({ table: row.table, row_id: row.row_id, seq: row.seq, reason: "invalid_row" }) });
      if (row.table === "expenses") expenseDiagnostics.set(row.row_id, [...expenseDiagnostics.get(row.row_id) ?? [], key]);
      // Keep at most 100 diagnostics; payloads and conversation text are never retained.
      const records = await meta.where("key").startsWith("sync:quarantine:").primaryKeys();
      records.sort((a, b) => Number(a.slice("sync:quarantine:".length)) - Number(b.slice("sync:quarantine:".length)));
      if (records.length > QUARANTINE_LIMIT) await meta.bulkDelete(records.slice(0, records.length - QUARANTINE_LIMIT));
    } catch (quarantineError) { console.warn("Could not record quarantine diagnostic:", quarantineError); }
    return false;
  }

  const local = await spec.table().get(row.row_id);
  // Last-write-wins, with one exception: a local row with unpushed changes AND a
  // newer timestamp keeps its edit and stays dirty.
  if (local && local._dirty === 1 && (stream.held.has(`${row.table}:${row.row_id}`) || local.updatedAt > row.updated_at)) return true;

  // `_extra` is replaced, never merged: it is "what the server's copy carries
  // that I do not know", and the server's copy just changed.
  const attribution = stream.ledger.kind === "family" ? { _createdBy: row.created_by_member, _updatedBy: row.updated_by_member } : {};
  await spec.table().put({ ...domain, ...attribution, ...(local && "_moveFrom" in local ? { _moveFrom: local._moveFrom } : {}), _dirty: 0, _syncedAt: new Date().toISOString(), _extra: extra });
  if (row.table === "expenses" && expenseDiagnostics.size > 0) {
    const resolved = expenseDiagnostics.get(row.row_id);
    if (resolved) {
      const meta = stream.ledger.db.meta;
      await meta.bulkDelete(resolved);
      expenseDiagnostics.delete(row.row_id);
    }
  }
  return true;
}

/** Downloads everything since the cursor. Returns how many rows it could not read. */
async function pullAll(stream: Stream): Promise<number> {
  let since = Number.parseInt((await stream.ledger.db.meta.get(CURSOR_KEY))?.value ?? "0", 10);
  if (!Number.isFinite(since) || since < 0) since = 0;
  let unreadable = 0;

  for (;;) {
    const res = await authedFetch(
      `${stream.path}/pull?since=${String(since)}&limit=${String(PULL_LIMIT)}`,
      { method: "GET" },
    );
    await checkStreamResponse(res, stream);
    if (res.status === 401) throw new SessionExpiredError();
    if (!res.ok) throw new SyncHttpError(`Could not download changes (HTTP ${String(res.status)})`, res.status);

    const body = (await res.json()) as {
      rows?: PullRow[];
      next_since?: number;
      has_more?: boolean;
    };
    const rows = body.rows ?? [];
    if (rows.length === 0) {
      // An empty page must not MOVE the cursor. The server echoes `since` back
      // when it has nothing newer; anything else would step this device past
      // rows it was never sent, and they would not be requested again.
      if (typeof body.next_since === "number" && body.next_since > since) {
        throw new Error(
          `The sync service skipped ahead on an empty page (${String(body.next_since)} after ${String(since)})`,
        );
      }
      return unreadable;
    }

    const expenseDiagnostics = new Map<string, string[]>();
    const meta = stream.ledger.db.meta;
    for (const record of await meta.where("key").startsWith("sync:quarantine:").toArray()) {
      try {
        const diagnostic: unknown = JSON.parse(record.value);
        if (!diagnostic || typeof diagnostic !== "object" || !("table" in diagnostic) || typeof diagnostic.table !== "string" || !("row_id" in diagnostic) || typeof diagnostic.row_id !== "string") throw new SyntaxError("Unreadable quarantine diagnostic.");
        if (diagnostic.table === "expenses") expenseDiagnostics.set(diagnostic.row_id, [...expenseDiagnostics.get(diagnostic.row_id) ?? [], record.key]);
      } catch (cause) {
        if (!(cause instanceof SyntaxError)) throw cause;
        try {
          await stream.ledger.db.transaction("rw", meta, async () => {
            await meta.put({ key: record.key.replace("sync:quarantine:", "sync:quarantine-unreadable:"), value: record.value });
            await meta.delete(record.key);
          });
        } catch (storageError) {
          console.warn(`Could not move unreadable quarantine diagnostic ${record.key}:`, storageError);
        }
        console.warn(`Could not read quarantine diagnostic ${record.key}:`, cause);
      }
    }
    for (const row of rows) {
      if (!(await importRow(row, stream, expenseDiagnostics))) unreadable += 1;
    }

    // Advance only after the whole page committed. `seq` is a monotonic server
    // cursor — strictly better than a timestamp watermark, which needed slack
    // for clock skew between writers.
    //
    // A quarantined row advances the cursor with the rest, deliberately. Holding
    // it back would re-download the same broken row on every sync forever, and
    // the user would be told about it every time with no way to act on it.
    //
    // The cursor must genuinely move. A server answering `has_more` with a
    // `next_since` that is absent or has not advanced would spin this loop on
    // the same page forever — the one place a malformed response could hang the
    // app rather than fail it (INV-7).
    if (typeof body.next_since !== "number" || body.next_since <= since) {
      throw new Error(
        `The sync service returned a cursor that does not advance (${String(body.next_since)} after ${String(since)})`,
      );
    }
    since = body.next_since;
    await stream.ledger.db.meta.put({ key: CURSOR_KEY, value: String(since) });
    if (body.has_more !== true) return unreadable;
  }
}

/* ── orchestration ─────────────────────────────────────────────────────────── */

export async function countPending(ledger?: LedgerRef): Promise<number> {
  const activeSpecs = ledger ? streamFor(ledger).specs : specs;
  const counts = await Promise.all(activeSpecs.map((s) => s.table().where("_dirty").equals(1).count()));
  return counts.reduce((a, b) => a + b, 0);
}

/**
 * Is it safe to seed a brand-new account's default categories yet? (F2)
 *
 * Only once a pull has actually **completed**, or when the device is genuinely
 * offline and there is nothing inbound to wait for.
 *
 * Pure, and exported, because the condition is subtler than it looks and the
 * cost of getting it wrong is duplicated data on every device. Two traps:
 *
 *   - `state === "idle"` alone is not enough. That is also the *initial* status,
 *     before any sync has run, and `subscribeSync` replays the current value to
 *     every new subscriber — so a naive check fires on mount and seeds eight
 *     categories a returning user already has.
 *   - Seeding waits until a pull has landed this session, or the device is
 *     offline. A later error or sync does not undo that completed pull.
 */
export function shouldSeedDefaults(s: SyncStatus): boolean {
  return firstPullSettled(s);
}

/**
 * Whether the inbound half of sync has settled — a pull completed, or the device
 * is offline and there is nothing inbound to wait for.
 *
 * The same condition {@link shouldSeedDefaults} needs, under the name its other
 * caller needs. The salary gate asks it before deciding an account has no
 * salary: on a second device the row arrives by sync, and a gate that fired on
 * an empty Dexie would demand a figure the account already has — then overwrite
 * it, because `setSalary` writes a new `effectiveFrom` version rather than
 * editing the one still in flight.
 *
 * Keyed on `lastSyncedAt`, not on the state being idle: once a pull has landed
 * it has landed, and a later cycle going back to "syncing" must not unmount the
 * salary form mid-entry. `resetSyncStatus()` nulls it, which is what takes an
 * account switch back to false.
 */
export function firstPullSettled(s: SyncStatus): boolean {
  if (s.state === "offline") return true;
  return s.lastSyncedAt !== null;
}

let inFlight: Promise<void> | null = null;
let again = false;
function shouldRunAgain(): boolean { return again; }
let familyInFlight: Promise<void> | null = null;
let familySyncSuspensions = 0;
let familySyncRequested = false;
/** A membership command must not race a pull into a store it is about to clear. */
export function suspendFamilySync(): () => void {
  familySyncSuspensions += 1;
  let resumed = false;
  return () => {
    if (resumed) return;
    resumed = true;
    familySyncSuspensions -= 1;
    if (familySyncSuspensions === 0 && familySyncRequested) {
      familySyncRequested = false;
      queueSync();
    }
  };
}
export function waitForFamilySync(): Promise<void> { return familyInFlight ?? Promise.resolve(); }
let pushing = false;
let familyPushing = false;
let protocolChecked = false;

/**
 * True while rows are being uploaded.
 *
 * `undoExpense` needs this: a row mid-push may already exist on the server, so
 * hard-deleting it locally would leave an orphan the next pull resurrects.
 */
export function isPushInFlight(ledger?: LedgerRef): boolean {
  return ledger?.kind === "family" ? familyPushing : pushing;
}

function unreadableMessage(count: number): string {
  const rows = count === 1 ? "1 row" : `${String(count)} rows`;
  return `${rows} could not be read and ${count === 1 ? "was" : "were"} skipped. Everything else is up to date.`;
}

/**
 * Deliberately prescribes nothing.
 *
 * The obvious remedy — sign out and back in — runs `clearLocalData()`, which
 * would destroy the user's *own* un-uploaded rows along with the foreign ones.
 * An error message must not talk someone into data loss, and this state should
 * be unreachable anyway, so it reports the fact and reassures about the part
 * that matters (C-8, INV-7).
 */
function foreignMessage(count: number): string {
  const rows = count === 1 ? "1 row" : `${String(count)} rows`;
  return `${rows} on this device ${count === 1 ? "belongs" : "belong"} to a different account and ${count === 1 ? "was" : "were"} not uploaded. Your own expenses are unaffected.`;
}

/**
 * The reassurance that rides on every dead-session message. The account is the
 * home of the ledger and this device holds a copy (SPEC §11.1), so "safe on this
 * device" undersold it — but rows that have not uploaded really do exist only
 * here, and saying so is what keeps anyone from signing out over them.
 */
const LEDGER_IS_SAFE =
  "Your ledger lives in your account, and anything not yet uploaded is saved on this device until it does.";

/**
 * Publish the dead-session state without a round-trip.
 *
 * A session that died in an earlier run leaves nothing to discover: the token is
 * already gone, so `sync()` returns at its own guard and never sets a status.
 * Without this the app would say **nothing at all** on relaunch whenever the
 * outbox happens to be empty — and an empty outbox does not mean a dead session
 * is free. Pushing is only half of sync: rows logged on another device stop
 * arriving too, silently and indefinitely.
 *
 * Same wording as the live path, so the user sees one message for one state.
 */
export async function reportSessionExpired(): Promise<void> {
  setStatus({
    state: "signed-out", retrying: false,
    pending: await countPending(),
    unreadable: 0,
    foreign: 0,
    error: `${new SessionExpiredError().message} ${LEDGER_IS_SAFE}`,
  });
}

export async function sync(): Promise<void> {
  if (inFlight) { again = true; await inFlight; await syncFamily(true); if (protocolChecked && !protocolUpdateRequired()) await syncLinkedSplits(); return; }
  inFlight = (async () => {
    do {
      again = false;
      await syncPersonal();
    } while (shouldRunAgain());
  })();
  try { await inFlight; } finally { inFlight = null; }
  if (readSession() !== null && (typeof navigator === "undefined" || navigator.onLine)) { await syncFamily(true); if (protocolChecked && !protocolUpdateRequired()) await syncLinkedSplits(); }
}

async function syncPersonal(): Promise<void> {

  // Checked BEFORE `navigator.onLine`, deliberately.
  //
  // With no session there is nothing to sync whether online or not, and the
  // offline branch below would overwrite `reportSessionExpired`'s message with
  // "Offline · N waiting to upload" — a promise the app cannot keep, because
  // reconnecting will not upload anything without a token. Every write calls
  // `queueSync()`, so a single expense logged offline during an expired session
  // was enough to replace the one message that explains why nothing is moving.
  //
  // Captured rather than discarded: the push guard compares every dirty row
  // against the account this token actually belongs to.
  const session = readSession();
  if (session === null) return;

  if (typeof navigator !== "undefined" && !navigator.onLine) {
    setStatus({
      state: "offline", retrying: false,
      pending: await countPending(),
      unreadable: 0,
      splitShareFailures: 0,
      foreign: 0,
      error: null,
    });
    return;
  }

  await (async () => {
    heldRows.clear();
    setStatus({ state: "syncing", retrying: false, error: null, held: 0, droppedPreferences: 0, splitShareFailures: 0 });
    try {
      const failedMoves = await recoverExpenseMoves();
      // Fails closed. A client pushing rows the server cannot parse corrupts its
      // own account.
      if (!protocolChecked) {
        await assertProtocolCompatible();
        protocolChecked = true;
      }

      const stream = streamFor(personalLedger(session.accountId));
      let foreign = 0;
      pushing = true;
      try {
        for (const spec of specs) {
          foreign += await pushSpec(spec as SyncSpec<Syncable>, session.accountId, stream);
        }
      } finally {
        pushing = false;
      }

      const unreadable = await pullAll(stream);
      // Preserve the committed pull's counts even if checking derived shares fails.
      const notices = [
        ...(failedMoves ? [`${failedMoves} move${failedMoves === 1 ? "" : "s"} could not be finished.`] : []),
        ...(unreadable > 0 ? [unreadableMessage(unreadable)] : []),
        ...(stream.droppedPreferences > 0 ? [droppedPreferencesMessage(stream.droppedPreferences)] : []),
        ...(foreign > 0 ? [foreignMessage(foreign)] : []),
      ];
      setStatus({ lastSyncedAt: new Date().toISOString(), moveRecoveryFailures: failedMoves, unreadable, foreign, droppedPreferences: stream.droppedPreferences });
      let splitShareFailures: number;
      try { splitShareFailures = await reconcileSplitShares(); }
      catch (cause) {
        const reason = cause instanceof Error ? cause.message : String(cause);
        setStatus({ state: "error", retrying: false, pending: await countPending(), error: [...notices, `Synced, but split shares couldn't be checked: ${reason}`].join(" ") });
        return;
      }
      if (splitShareFailures) notices.push(`${splitShareFailures} split share${splitShareFailures === 1 ? "" : "s"} could not be updated — open Splits to fix`);
      setStatus({
        state: "idle", retrying: false,
        pending: await countPending(),
        lastSyncedAt: new Date().toISOString(),
        unreadable,
        splitShareFailures,
        droppedPreferences: stream.droppedPreferences,
        foreign,
        error: notices.length > 0 ? notices.join(" ") : null,
      });
    } catch (cause) {
      // `unreadable` and `foreign` are reset on every failure path. Leaving a
      // previous run's count in place while showing a network message would put
      // two unrelated numbers on screen, only one of which is rendered.
      if (cause instanceof SessionExpiredError) {
        setStatus({
          state: "signed-out", retrying: false,
          pending: await countPending(),
          unreadable: 0,
          splitShareFailures: 0,
          foreign: 0,
          error: `${cause.message} ${LEDGER_IS_SAFE}`,
        });
        return;
      }
      const retrying = isTransientSyncFailure(cause);
      const detail = isNetworkFailure(cause)
        ? "Couldn't reach the server"
        : cause instanceof Error ? cause.message : String(cause);
      setStatus({
        state: "error",
        retrying,
        pending: await countPending(),
        unreadable: 0,
        splitShareFailures: 0,
        foreign: 0,
        error: cause instanceof ProtocolMismatchError
          ? cause.message
          : retrying
            ? `${detail}. Trying again automatically — anything not yet uploaded is saved on this device.`
            : `${detail}. Anything not yet uploaded is saved on this device, and syncing tries again when you next save or open the app.`,
      });
    }
  })();
}

/**
 * A fetch that never reached the server. Browsers reject with a TypeError and
 * their own wording; matched by that wording, not by the type alone, so a
 * TypeError from a bug in this file keeps its message rather than being
 * blamed on the network (INV-7).
 */
const NETWORK_FAILURE = /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?|Network request failed)$/;
function isNetworkFailure(cause: unknown): boolean {
  return cause instanceof TypeError && NETWORK_FAILURE.test(cause.message);
}

const HTTP_SERVER_ERROR = 500;
const HTTP_REQUEST_TIMEOUT = 408;
const HTTP_TOO_MANY_REQUESTS = 429;

/**
 * Worth a timed retry: the network, a server error, a timeout or rate limit.
 * Anything else — a refused request, a malformed reply — will fail the same
 * way again, so it waits for the next write or return to the app instead.
 */
function isTransientSyncFailure(cause: unknown): boolean {
  if (protocolUpdateRequired()) return false;
  return isNetworkFailure(cause) || (cause instanceof SyncHttpError &&
    (cause.status >= HTTP_SERVER_ERROR || cause.status === HTTP_REQUEST_TIMEOUT || cause.status === HTTP_TOO_MANY_REQUESTS));
}

/* ── triggers ──────────────────────────────────────────────────────────────── */

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

/** Ask for a sync soon. Safe to call after every write. */
export function queueSync(delayMs = 1_500): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void sync();
  }, delayMs);
}

/** Wire up the ambient triggers. Returns a teardown for tests. */
export function startSyncTriggers(): () => void {
  const retry = createSyncRetry({
    run: () => void sync(),
    setTimer: (callback, delayMs) => setTimeout(callback, delayMs),
    clearTimer: (timer) => clearTimeout(timer),
    isHidden: () => document.visibilityState === "hidden",
    isRetryable: (s) => s.state === "error" && s.retrying,
  });
  const unsubscribe = subscribeSync(retry.onStatus);
  const onOnline = (): void => void sync();
  const onVisible = (): void => {
    if (document.visibilityState === "visible") void sync();
  };

  const onOffline = (): void => setStatus({ state: "offline", retrying: false });

  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);
  document.addEventListener("visibilitychange", onVisible);

  void sync();

  return () => {
    retry.stop();
    unsubscribe();
    window.removeEventListener("online", onOnline);
    window.removeEventListener("offline", onOffline);
    document.removeEventListener("visibilitychange", onVisible);
  };
}

/**
 * Forget everything this module believes about the ledger it was syncing.
 *
 * Called wherever `clearLocalData()` is — the status is a set of claims about
 * rows that a wipe has just destroyed, and `lastSyncedAt` in particular is the
 * claim "a pull has completed for this account". Left standing across an in-tab
 * account switch, `firstPullSettled` answers true for the *previous* account, so
 * the salary gate concludes the new one has no salary before its first pull has
 * run — and a figure entered there writes a fresh `effectiveFrom` row over the
 * one still arriving.
 *
 * Subscribers are notified, because a stale status that nobody is told about is
 * the same bug one render later.
 */
export function resetSyncStatus(): void {
  resetLinkedSync();
  status = CLEAN_STATUS;
  setFamilyStatus({ ...CLEAN_STATUS });
  for (const fn of listeners) fn(status);
}

/** Reset module state. Test-only. */
export function resetSyncStateForTest(): void {
  resetLinkedSync();
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = null;
  inFlight = null;
  again = false;
  familyInFlight = null;
  pushing = false;
  familyPushing = false;
  protocolChecked = false;
  status = CLEAN_STATUS;
  setFamilyStatus({ ...CLEAN_STATUS });
}

/** Family failures have their own observable status and never escape into personal sync. */
export async function syncFamily(recoveryAlreadyRun = false): Promise<void> {
  if (familySyncSuspensions > 0) { familySyncRequested = true; return; }
  if (familyInFlight) return familyInFlight;
  familyInFlight = runFamily(recoveryAlreadyRun);
  try { await familyInFlight; } finally { familyInFlight = null; }
}
async function runFamily(recoveryAlreadyRun: boolean): Promise<void> {
  try {
    const cachedId = await getMeta(FAMILY_ID_KEY);
    const removed = cachedId || familyDb.isOpen() ? await familyDb.meta.get(FAMILY_REMOVED_KEY) : undefined;
    if (removed) {
      if (await resolveEmptyRemoval(removed.value)) return;
      setFamilyStatus({ state: "error", pending: await countPending(familyLedger(removed.value)), error: "Family membership ended. Choose copy or discard for local changes.", retrying: false });
      return;
    }
    if (!protocolChecked || protocolUpdateRequired() || readSession() === null || (typeof navigator !== "undefined" && !navigator.onLine)) return;
    const failedMoves = recoveryAlreadyRun ? 0 : await recoverExpenseMoves();
    const membership = await getFamily();
    if (!cachedId && !membership && !familyDb.isOpen()) { setFamilyStatus({ ...CLEAN_STATUS }); return; }
    const cached = cachedId ?? await localLedgerAccountId(familyDb);
    if (!membership) {
      if (cached) {
        await familyDb.meta.put({ key: FAMILY_REMOVED_KEY, value: cached });
        if (await resolveEmptyRemoval(cached)) return;
        setFamilyStatus({ state: "error", pending: await countPending(familyLedger(cached)), error: "Family membership ended. Choose copy or discard for local changes.", retrying: false });
      } else setFamilyStatus({ ...CLEAN_STATUS });
      return;
    }
    if (cached && cached !== membership.family_id) {
      await familyDb.meta.put({ key: FAMILY_REMOVED_KEY, value: cached });
      if (await resolveEmptyRemoval(cached)) return;
      throw new Error("Previous family data must be copied or discarded before loading a new family.");
    }
    await setMeta(FAMILY_VIEW_KEY, JSON.stringify(membership));
    await setMeta(FAMILY_ID_KEY, membership.family_id);
    const stream = streamFor(familyLedger(membership.family_id));
    setFamilyStatus({ state: "syncing", error: null, held: 0, droppedPreferences: 0, unreadable: 0, foreign: 0, retrying: false });
    let foreign = 0;
    familyPushing = true;
    try {
      for (const spec of stream.specs) foreign += await pushSpec(spec, stream.ledger.ownerId, stream);
    } finally { familyPushing = false; }
    const unreadable = await pullAll(stream);
    setFamilyStatus({ moveRecoveryFailures: failedMoves, state: "idle", lastSyncedAt: new Date().toISOString(), pending: await countPending(stream.ledger), foreign, unreadable, droppedPreferences: stream.droppedPreferences,
      error: [failedMoves ? `${failedMoves} move${failedMoves === 1 ? "" : "s"} could not be finished.` : "", unreadable ? unreadableMessage(unreadable) : "", stream.droppedPreferences ? droppedPreferencesMessage(stream.droppedPreferences) : "", foreign ? foreignMessage(foreign) : ""].filter(Boolean).join(" ") || null });
  } catch (cause) {
    let pending: number | null = null;
    try {
      const removed = await familyDb.meta.get(FAMILY_REMOVED_KEY);
      if (removed && await resolveEmptyRemoval(removed.value)) return;
      pending = await countPending(familyLedger(removed?.value ?? (await getMeta(FAMILY_ID_KEY)) ?? ""));
    }
    catch (countError) { console.warn("Could not count pending family changes:", countError); }
    setFamilyStatus({ state: "error", pending, retrying: false, unreadable: 0, foreign: 0,
      error: cause instanceof Error ? cause.message : String(cause) });
  }
}

function droppedPreferencesMessage(count: number): string {
  return `${count} invalid capture order ${count === 1 ? "preference was" : "preferences were"} dropped. Your settings and expenses were retained; reorder Categories to choose a new order.`;
}

export function reportMoveRecoveryFailures(count: number): void {
  const other = status.error?.replace(/\d+ moves? could not be finished\.\s*/g, "").trim();
  setStatus({ moveRecoveryFailures: count, error: [count ? `${count} move${count === 1 ? "" : "s"} could not be finished.` : "", other].filter(Boolean).join(" ") || null });
}
